import { VerityApiContext } from "../../api/fetch";
import { useContext, useEffect, useId, useRef, useState } from "react";
import { CoreModal, CorePaper, CoreRadioGroup, CoreTextField, CoreTitleCard, coreToast, RadioOption } from "../../core";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import { Tooltip, Typography } from "@mui/material";
import {
	ProctoringOption,
	ProctoringOptionProfileV2,
	Application,
	OptionRelatedInstituteSettings,
	Extension,
} from "@verity/proctoring-option/types";
import {
	DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
	enforceConfiguration,
	isCourseProfile,
	isInstituteProfile,
	isUserProfile,
} from "@verity/proctoring-option";
import { VerityInstituteSettings } from "../../types/common";
import { categorizeProctoringOptions, ConfigurationSummaryList } from "../proctoring-options/configurationSummaryList";
import i18nPackage from "../../i18n/i18n";
import styles from "./viewProfile.module.css";
import { getPlatformTextFromProctoringOptions } from "../proctoring-options/option-text";

export type ViewProfileModalProps = {
	title: string;
	description?: string;
	profileTitle: string;
	open: boolean;
	onClose: () => void;
	lmsCourseId?: string;
	width?: string;
	height?: string;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings?: VerityInstituteSettings;
} & (
	| {
			profiles: Array<ProctoringOptionProfileV2>;
			proctoringOption: Partial<ProctoringOption>;
			profile?: undefined;
			viewOnly: false;
			setActiveProfileId: (id: number | undefined) => void;
			refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
			hasInstituteProfilePermissions: boolean;
			setProctoringOption: ((options: Partial<ProctoringOption>) => void) | undefined;
	  }
	| {
			profiles?: undefined;
			proctoringOption?: undefined;
			profile: ProctoringOptionProfileV2;
			viewOnly: true;
			setActiveProfileId?: undefined;
			refreshProfiles?: undefined;
			hasInstituteProfilePermissions?: undefined;
			setProctoringOption?: undefined;
	  }
);

const ViewProfileModal = ({
	profiles,
	profile,
	title,
	description,
	proctoringOption,
	open,
	profileTitle,
	viewOnly,
	refreshProfiles,
	setActiveProfileId,
	onClose,
	lmsCourseId,
	hasInstituteProfilePermissions,
	width,
	height,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	setProctoringOption,
}: ViewProfileModalProps) => {
	const { createProctoringProfile, getAllApplications, getAllExtensions } = useContext(VerityApiContext);
	const [profileName, setProfileName] = useState<string>(profileTitle);
	const [publishLocation, setPublishLocation] = useState<"institution" | "course" | "user" | undefined>(
		viewOnly ? undefined : "user",
	);
	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);
	const isMobileLayout = useMobileLayout();
	const radioGroupAriaId = useId();

	useEffect(() => {
		async function fetchApplicationData() {
			const application_list = await getAllApplications();
			setApplications(application_list);
		}

		async function fetchExtensionData() {
			const extension_list = await getAllExtensions();
			setExtensions(extension_list);
		}

		void fetchApplicationData();
		void fetchExtensionData();
	}, []);

	const option = proctoringOption ?? profile.proctoringOption;

	const { optionCategories, optionChildren } = categorizeProctoringOptions(
		option,
		verityInstituteSettings,
		optionRelatedInstituteSettings,
	);

	const textRef = useRef<HTMLDivElement>(null);
	const [overflow, setOverflow] = useState(false);

	useEffect(() => {
		if (textRef.current) {
			setOverflow(textRef.current.offsetWidth < textRef.current.scrollWidth);
		}
	}, [profileName]);

	let displayProfileName = (
		<span
			style={{
				width: "100%",
				maxWidth: "100%",
				overflow: "hidden",
				whiteSpace: "nowrap",
				textOverflow: "ellipsis",
			}}
			ref={textRef}
		>
			{profileName}
		</span>
	);

	if (overflow) {
		displayProfileName = (
			<Tooltip title={profileName} arrow>
				{displayProfileName}
			</Tooltip>
		);
	}

	const radioOptions: RadioOption[] = [
		...(hasInstituteProfilePermissions
			? [
					{
						value: "institution",
						label: (
							<>
								<b>{i18nPackage.t("editQuizPage.profileSectionTSX.profileType.institute")}:</b>{" "}
								{i18nPackage.t("editQuizPage.saveAsProfile.instituteProfileDesc")}
							</>
						),
					},
				]
			: []),
		...(lmsCourseId
			? [
					{
						value: "course",
						label: (
							<>
								<b>{i18nPackage.t("editQuizPage.profileSectionTSX.profileType.course")}:</b>{" "}
								{i18nPackage.t("editQuizPage.saveAsProfile.courseProfileDesc")}
							</>
						),
					},
				]
			: []),
		{
			value: "user",
			label: (
				<>
					<b>{i18nPackage.t("editQuizPage.profileSectionTSX.profileType.user")}:</b>{" "}
					{i18nPackage.t("editQuizPage.saveAsProfile.personalProfileDesc")}
				</>
			),
		},
	];

	return (
		<CoreModal
			title={isMobileLayout ? undefined : title}
			open={open}
			onClose={onClose}
			minWidth={width ?? (isMobileLayout ? undefined : "min(80%, 960px)")}
			widthFixed
			heightFixed={isMobileLayout}
			padding={isMobileLayout ? undefined : "24px 24px 0 24px"}
			maxHeight={height}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.saveAsProfile.cancel"),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.saveAsProfile.save"),
					color: "primary",
					disabled: !profileName,
					hide: viewOnly,
					onClick: () => {
						if (proctoringOption && !viewOnly) {
							const setLmsCourseId = publishLocation === "course" && lmsCourseId ? lmsCourseId : undefined;
							const isInstituteProfile = publishLocation === "institution";
							createProctoringProfile({
								proctoringOption: proctoringOption,
								isInstituteProfile: isInstituteProfile,
								lmsProvidedCourseId: setLmsCourseId,
								name: profileName.trim(),
							})
								.then((newProfile) => {
									const profilesCopy = Array.from(profiles);
									if (newProfile) profilesCopy.unshift(newProfile);
									void refreshProfiles();
									setActiveProfileId(newProfile?.id);
									setProctoringOption?.(newProfile?.proctoringOption); // the proctoringOptions in newProfile are non-null-ified, so we set the proctoringOption so the deep equal works (in case the current proctoring options still have null values).
									coreToast.success(i18nPackage.t("editQuizPage.saveAsProfile.success"));
								})
								.catch((e) => {
									console.error(e);
									coreToast.error(i18nPackage.t("editQuizPage.saveAsProfile.error"));
								})
								.finally(onClose);
						}
					},
				},
			]}
		>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => (
					<div className={`${styles.pageContainer} ${styles.pageContainerCompact}`} tabIndex={0}>
						{children}
					</div>
				)}
			>
				{isMobileLayout && (
					<CoreTitleCard
						title={title}
						description={description ?? i18nPackage.t("editQuizPage.viewProfile.description")}
						style={{ minWidth: "300px", padding: "15px 10px 10px 10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
				)}
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => (
						<CorePaper component="section" className={styles.profilePage} sx={{ padding: "10px" }}>
							<div style={{ display: "flex", flexWrap: "wrap", maxWidth: "100%" }}>{children}</div>
						</CorePaper>
					)}
					whenFalse={(children) => (
						<div className={styles.pageContainer} tabIndex={0}>
							{children}
						</div>
					)}
				>
					{!isMobileLayout && (
						<div style={{ paddingBottom: "25px" }}>
							<Typography color={"#757575"}>
								{description ?? i18nPackage.t("editQuizPage.saveAsProfile.description")}
							</Typography>
						</div>
					)}

					<div
						style={{
							display: "flex",
							flexBasis: "100%",
							borderBottom: "1px solid #898989",
							padding: "0px 0px 10px 0px",
						}}
					>
						<Typography variant="h3" fontSize={16} fontWeight={600}>
							{i18nPackage.t("editQuizPage.saveAsProfile.profileDetails")}
						</Typography>
					</div>

					<div
						style={{
							display: "flex",
							alignItems: isMobileLayout ? "center" : undefined,
							margin: "20px 0px 0px",
							flexBasis: "100%",
							gap: "10px",
						}}
					>
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "flex-start",
								alignContent: "center",
								width: "100%",
								maxWidth: isMobileLayout ? "130px" : "200px",
								minWidth: isMobileLayout ? "130px" : "200px",
								paddingRight: isMobileLayout ? undefined : "15px",
								marginRight: isMobileLayout ? "5px" : undefined,
							}}
						>
							<Typography fontWeight={600} color={"#595959"}>
								{i18nPackage.t("editQuizPage.saveAsProfile.platform")}
							</Typography>
						</div>
						<div style={{ display: "flex", alignItems: "flex-start", width: "60%" }}>
							<Typography>
								{getPlatformTextFromProctoringOptions(
									enforceConfiguration(
										DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
										option,
										verityInstituteSettings,
										optionRelatedInstituteSettings,
									).proctoringOptions,
								)}
							</Typography>
						</div>
					</div>

					<div
						style={{
							display: "flex",
							alignItems: isMobileLayout ? "center" : undefined,
							margin: "20px 0px 0px",
							flexBasis: "100%",
							width: isMobileLayout ? "100%" : undefined,
							maxWidth: isMobileLayout ? "100%" : undefined,
							gap: "10px",
						}}
					>
						<div
							style={{
								display: "flex",
								flexWrap: isMobileLayout ? undefined : "wrap",
								alignItems: "flex-start",
								alignContent: "center",
								width: "100%",
								maxWidth: isMobileLayout ? "130px" : "200px",
								minWidth: isMobileLayout ? "130px" : "200px",
								paddingRight: isMobileLayout ? undefined : viewOnly ? "15px" : "11px",
								marginRight: isMobileLayout ? "5px" : undefined,
							}}
						>
							<Typography fontWeight={600} color={"#595959"}>
								{i18nPackage.t("editQuizPage.saveAsProfile.profileName")}
							</Typography>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "flex-start",
								flex: isMobileLayout ? undefined : 1,
								width: isMobileLayout ? "100%" : undefined,
								maxWidth: isMobileLayout ? "100%" : undefined,
							}}
						>
							{viewOnly && <Typography>{isMobileLayout ? displayProfileName : profileName}</Typography>}
							{!viewOnly && (
								<CoreTextField
									sx={{ width: "100%" }}
									value={profileName}
									autoComplete="off"
									inputProps={{ maxLength: 30 }}
									label={i18nPackage.t("editQuizPage.viewProfile.enterProfileName")}
									onChange={(event) => setProfileName(event.target.value)}
									disabled={viewOnly}
								/>
							)}
						</div>
					</div>

					{viewOnly && (
						<div
							style={{
								display: "flex",
								margin: "20px 0px 0px",
								flexBasis: "100%",
								gap: "10px",
							}}
						>
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									alignItems: "flex-start",
									alignContent: "center",
									width: "100%",
									maxWidth: isMobileLayout ? "130px" : "200px",
									minWidth: isMobileLayout ? "130px" : "200px",
									paddingRight: isMobileLayout ? undefined : "15px",
									marginRight: isMobileLayout ? "5px" : undefined,
								}}
							>
								<Typography fontWeight={600} color={"#595959"}>
									{i18nPackage.t("editQuizPage.saveAsProfile.profileCategory")}
								</Typography>
							</div>
							<div style={{ display: "flex", alignItems: "flex-start", width: "60%" }}>
								<Typography>
									{" "}
									{isInstituteProfile(profile)
										? i18nPackage.t("editQuizPage.profileSectionTSX.profileType.institute")
										: isCourseProfile(profile)
											? i18nPackage.t("editQuizPage.profileSectionTSX.profileType.course")
											: isUserProfile(profile)
												? i18nPackage.t("editQuizPage.profileSectionTSX.profileType.user")
												: "None"}
								</Typography>
							</div>
						</div>
					)}

					{Object.keys(optionCategories).length > 0 && (
						<>
							<div
								style={{
									display: "flex",
									flexBasis: "100%",
									borderBottom: "1px solid #898989",
									padding: "30px 0px 10px 0px",
									marginBottom: "12px",
								}}
							>
								<Typography variant="h3" fontWeight={700} fontSize={16}>
									{i18nPackage.t("editQuizPage.saveAsProfile.proctorConfig")}
								</Typography>
							</div>
							<ConfigurationSummaryList
								optionCategories={optionCategories}
								optionChildren={optionChildren}
								applications={applications}
								extensions={extensions}
							/>
						</>
					)}

					{!viewOnly && (
						<>
							<div
								id={radioGroupAriaId}
								style={{
									display: "flex",
									flexBasis: "100%",
									borderBottom: "1px solid #898989",
									padding: "5px 0px 10px 0px",
								}}
							>
								<Typography fontWeight={700}>{i18nPackage.t("editQuizPage.saveAsProfile.publish")}</Typography>
							</div>
							<div
								style={{
									display: "flex",
									margin: "20px 0px 10px",
									flexDirection: "column",
									gap: "16px",
								}}
							>
								<CoreRadioGroup
									row={false}
									onChange={(_e, value) => setPublishLocation(value as typeof publishLocation)}
									radioGroupAriaLabelledby={radioGroupAriaId}
									radioOptions={radioOptions}
									selectedValue={publishLocation}
									radioProps={{ style: { paddingTop: "5px" } }}
									formControlLabelProps={{
										style: { alignItems: "flex-start", margin: isMobileLayout ? "0px" : undefined },
									}}
								/>
							</div>
						</>
					)}
				</ConditionalWrapper>
			</ConditionalWrapper>
		</CoreModal>
	);
};

export default ViewProfileModal;
